import { AlertTriangle, CheckCircle2, Clock, XCircle } from "lucide-react";
import { HelpTip } from "./HelpTip";

export const STATUS_HELP = {
  held: "Can't be delivered yet. Open the invoice to see why and fix it.",
  ready: "Nothing is blocking it. It goes on the next sync, or click Sync Now.",
  synced: "Delivered into the receiver's accounting system.",
  failed: "The accounting system rejected it. Read the error, fix it, then click Sync Now.",
};

// Plain operator-facing status: Held / Ready / Synced / Failed + one-line why.
export default function StatusPill({ invoice, withHelp }) {
  const ss = invoice.sync_status;
  let kind = "ready";
  let label = "Ready";
  let why = null;

  if (ss === "synced") { kind = "synced"; label = "Synced"; }
  else if (ss === "failed") {
    kind = "failed"; label = "Failed";
    why = invoice.held_reason || "connector error";
  }
  else if (ss === "needs_mapping") {
    kind = "held"; label = "Held";
    why = invoice.held_reason || "item mapping needed";
  }
  else if (ss === "awaiting_sender_approval") {
    kind = "held"; label = "Held";
    why = "awaiting vendor approval";
  }
  else if (ss === "held_billing") {
    kind = "held"; label = "Held";
    why = (invoice.held_reason || "").includes("add a card") ? "add a card to receive" : "billing";
  }
  else if (ss === "sender_blocked") {
    kind = "held"; label = "Held";
    why = "vendor blocked";
  }
  else if (ss === "no_match") {
    kind = "held"; label = "Held";
    // Backend stamps a specific reason (no matching company vs unmatched
    // email); fall back to plain copy if it's missing on legacy rows.
    const r = (invoice.held_reason || "").toLowerCase();
    if (r.includes("unmatched email")) why = "unmatched email";
    else why = "no matching company";
  }
  else if (ss === "pending") { kind = "ready"; label = "Ready"; }

  const cls = {
    synced: "bg-emerald-50 text-emerald-900 border-emerald-300",
    failed: "bg-red-50 text-red-900 border-red-300",
    held: "bg-amber-50 text-amber-900 border-amber-300",
    ready: "bg-neutral-100 text-neutral-700 border-neutral-300",
  }[kind];
  const Icon = {
    synced: CheckCircle2, failed: XCircle, held: AlertTriangle, ready: Clock,
  }[kind];

  return (
    <div className={`inline-flex flex-col gap-0.5 px-2 py-1 border ${cls}`}
         data-testid={`status-pill-${invoice.invoice_number || invoice.id}`}
         data-status={label.toLowerCase()}>
      <div className="flex items-center gap-1 text-xs uppercase tracking-widest">
        <Icon size={10} /> {label}
        {withHelp && (
          <HelpTip text={STATUS_HELP[kind]} anchor="invoice-statuses" label={`What ${label} means`}
                   testid={`status-help-${invoice.invoice_number || invoice.id}`} />
        )}
      </div>
      {why && <div className="text-[10px] leading-tight opacity-75">{why}</div>}
    </div>
  );
}
